
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User, Video, CreditCard, AlertCircle } from "lucide-react";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { format, parseISO, isAfter, isBefore, addMinutes, subMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import VideoCallButton from '../videocall/VideoCallButton';

interface UpcomingSessionsModuleProps {
  onRefresh: () => void;
}

const UpcomingSessionsModule: React.FC<UpcomingSessionsModuleProps> = ({
  onRefresh
}) => {
  const { user, profile } = useAuth();
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadUpcomingAppointments();
    }
  }, [user]);

  const loadUpcomingAppointments = async () => {
    try {
      setLoading(true);
      
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          *,
          psychologists (id, name, photo_url, specialties)
        `)
        .eq('user_id', user.id)
        .gte('date', today)
        .in('status', ['confirmed', 'pending'])
        .order('date', { ascending: true })
        .order('time', { ascending: true });

      if (error) throw error;
      setAppointments(data || []);
    } catch (error) {
      console.error('Error loading upcoming appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMercadoPagoPayment = async (appointmentId: string, appointment: any) => {
    if (processingPayment === appointmentId) return;
    
    try {
      setProcessingPayment(appointmentId);
      
      const patientName = profile?.full_name || 
        (profile?.first_name && profile?.last_name ? `${profile.first_name} ${profile.last_name}` : 'Paciente');

      console.log('💳 Iniciando checkout do Mercado Pago para appointment:', appointmentId);
      toast.info('Criando checkout...', { description: 'Aguarde enquanto preparamos o pagamento' });

      const { data, error: checkoutError } = await supabase.functions.invoke('mercado-pago-checkout', {
        body: {
          appointmentId: appointmentId,
          amount: appointment.total_price,
          psychologistName: appointment.psychologists?.name,
          patientName: patientName,
          appointmentDate: appointment.date,
          appointmentTime: appointment.time,
          duration: appointment.duration
        }
      });

      console.log('📨 Resposta do checkout:', { data, error: checkoutError });

      if (checkoutError) {
        console.error('❌ Erro no checkout:', checkoutError);
        throw new Error(checkoutError.message || 'Erro ao processar pagamento');
      }

      if (!data?.checkoutUrl) {
        console.error('❌ URL de checkout não retornada:', data);
        throw new Error('URL de checkout não foi gerada');
      }
      
      console.log('🚀 Redirecionando para:', data.checkoutUrl);
      toast.success('Checkout criado!', { description: 'Redirecionando para o Mercado Pago...' });
      
      // Redirecionar para o checkout do Mercado Pago
      setTimeout(() => {
        console.log('🔄 Executando redirecionamento...');
        window.location.href = data.checkoutUrl;
      }, 500);
      
    } catch (error: any) {
      console.error('❌ Erro no pagamento:', error);
      const errorMessage = error?.message || "Erro ao gerar pagamento. Tente novamente.";
      toast.error('Erro no pagamento', { description: errorMessage });
    } finally {
      setProcessingPayment(null);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch (e) {
      return dateStr;
    }
  };

  const formatTime = (timeStr: string) => {
    try {
      return timeStr.substring(0, 5);
    } catch (e) {
      return timeStr;
    }
  };

  const getStatusBadge = (status: string, paymentStatus: string) => {
    if (paymentStatus !== 'paid') {
      return <Badge variant="destructive">Pagamento Pendente</Badge>;
    }
    
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-800">Confirmada</Badge>;
      case 'pending':
        return <Badge variant="secondary">Aguardando Confirmação</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const canJoinCall = (appointment: any) => {
    if (!appointment.meet_link || appointment.payment_status !== 'paid') return false;
    
    const now = new Date();
    const appointmentDateTime = parseISO(`${appointment.date}T${appointment.time}`);
    const startTime = subMinutes(appointmentDateTime, 15);
    const endTime = addMinutes(appointmentDateTime, appointment.duration + 15);
    
    return isAfter(now, startTime) && isBefore(now, endTime);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  if (appointments.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-lg font-medium mb-2">Nenhuma sessão agendada</p>
          <p className="text-muted-foreground mb-4">Que tal agendar uma consulta?</p>
          <Button asChild>
            <a href="/schedule">Agendar Consulta</a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {appointments.map((appointment) => (
        <Card key={appointment.id} className="hover:shadow-md transition-shadow">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div className="flex items-center space-x-3">
                {appointment.psychologists?.photo_url && (
                  <img
                    src={appointment.psychologists.photo_url}
                    alt={appointment.psychologists.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                )}
                <div>
                  <CardTitle className="text-lg">{appointment.psychologists?.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {appointment.psychologists?.specialties?.[0]}
                  </p>
                </div>
              </div>
              
              <div className="text-right">
                {getStatusBadge(appointment.status, appointment.payment_status)}
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{formatDate(appointment.date)}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {formatTime(appointment.time)} ({appointment.duration} minutos)
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(appointment.total_price)}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                {appointment.payment_status !== 'paid' && (
                  <div className="flex items-center space-x-2 text-orange-600">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm">Pagamento pendente</span>
                  </div>
                )}
                
                <div className="flex flex-col gap-2">
                  {appointment.payment_status !== 'paid' ? (
                    <Button 
                      onClick={() => handleMercadoPagoPayment(appointment.id, appointment)}
                      disabled={processingPayment === appointment.id}
                      className="w-full"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      {processingPayment === appointment.id ? 'Processando...' : 'Realizar Pagamento'}
                    </Button>
                  ) : appointment.meet_link ? (
                    <VideoCallButton
                      appointmentId={appointment.id}
                      date={appointment.date}
                      time={appointment.time}
                      duration={appointment.duration}
                      status={appointment.status}
                      meetLink={appointment.meet_link}
                      role="patient"
                    />
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-2">
                      Link da videochamada será disponibilizado em breve
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {appointment.notes && (
              <div className="mt-4 p-3 bg-muted rounded-md">
                <p className="text-sm">
                  <strong>Observações:</strong> {appointment.notes}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default UpcomingSessionsModule;
